import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check, ChevronDown, ChevronRight, RefreshCw, Trash2, X, Plus, Search,
} from "lucide-react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

/**
 * Filterable single-select combobox. The dropdown stays open while the
 * input is focused, the displayed value is whatever `renderItem` returns
 * for the selected option, and arrow keys + Enter work for keyboard nav.
 * Kept inline so we don't have to introduce a dependency.
 */
function Combobox({
  value, onChange, items, getKey, getSearch, renderItem,
  placeholder, disabled, emptyText = "No matches",
  testId,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 30);
    return items.filter((it) => getSearch(it).toLowerCase().includes(q)).slice(0, 50);
  }, [items, query, getSearch]);

  useEffect(() => { setFocusIdx(0); }, [filtered]);

  // close on outside click
  useEffect(() => {
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function selected(idx) {
    const it = filtered[idx];
    if (it == null) return;
    onChange?.(it);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKey(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(filtered.length - 1, i + 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); selected(focusIdx); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className={`flex items-center gap-1.5 input ${disabled ? "opacity-60" : ""}`}>
        <Search size={13} className="text-neutral-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          placeholder={value ? renderItem(value, { compact: true }) : placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          className="flex-1 bg-transparent outline-none text-[13px] min-w-0"
          autoComplete="off"
          data-testid={testId}
        />
        {value && !query && (
          <button type="button" onClick={() => { onChange?.(null); setQuery(""); }} title="Clear" className="text-neutral-400 hover:text-black shrink-0">
            <X size={12} />
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-black/10 rounded-md shadow-lg max-h-72 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[12.5px] text-neutral-500">{emptyText}</div>
          ) : filtered.map((it, i) => {
            const isFocus = i === focusIdx;
            const isSelected = value && getKey(value) === getKey(it);
            return (
              <button
                type="button"
                key={getKey(it)}
                onMouseEnter={() => setFocusIdx(i)}
                onMouseDown={(e) => e.preventDefault()}  // keep focus on input
                onClick={() => selected(i)}
                className={`w-full text-left px-3 py-1.5 text-[12.5px] flex items-center justify-between gap-2 ${
                  isFocus ? "bg-neutral-100" : "bg-white"
                }`}
              >
                <span className="min-w-0 truncate">{renderItem(it)}</span>
                {isSelected && <Check size={12} className="text-emerald-700 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
}
function fmtNum(n) { return typeof n === "number" ? n.toLocaleString() : "—"; }

function StatusPill({ status }) {
  const isArmed = status === "armed";
  return (
    <span className={`inline-block text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${
      isArmed ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"
    }`}>
      {status || "—"}
    </span>
  );
}

function ArmModal({ open, onClose, onArmed }) {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [program, setProgram] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Reset on close.
  useEffect(() => {
    if (!open) {
      setUser(null); setProgram(null); setPrograms([]); setBusy(false); setErr("");
    }
  }, [open]);

  // Lazy-load users when modal opens.
  useEffect(() => {
    if (!open || users.length > 0) return;
    setUsersLoading(true);
    api.get("/admin/users").then(({ data }) => {
      setUsers((data || []).filter((u) => u.user_id));
    }).catch((e) => {
      setErr(e?.response?.data?.detail || e?.message || "user_load_failed");
    }).finally(() => setUsersLoading(false));
  }, [open, users.length]);

  // Load the picked user's programs.
  useEffect(() => {
    if (!user) { setPrograms([]); setProgram(null); return; }
    setProgramsLoading(true);
    api.get(`/admin/users/${encodeURIComponent(user.user_id)}/programs`).then(({ data }) => {
      setPrograms(data || []);
      setProgram(null);
    }).catch((e) => {
      setErr(e?.response?.data?.detail || e?.message || "program_load_failed");
      setPrograms([]);
    }).finally(() => setProgramsLoading(false));
  }, [user]);

  if (!open) return null;

  async function submit(e) {
    e?.preventDefault();
    setErr("");
    if (!user || !program) {
      setErr("Pick a user and a program.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/net-traces/arm", {
        user_id: user.user_id,
        program_id: program.id,
      });
      onArmed?.(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "arm_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-[14px] w-full max-w-md p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="arm-trace-modal"
      >
        <div className="flex items-center justify-between">
          <div className="font-display text-xl font-extrabold">Arm a sync trace</div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-black"><X size={18} /></button>
        </div>
        <p className="text-[12.5px] text-neutral-500 mt-1.5 leading-relaxed">
          The next time this user opens this program via their dashboard, the extension
          captures every network call, the rendered HTML, and step-machine events to a new trace.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3.5">
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">
              User {usersLoading && <span className="opacity-60 normal-case">loading…</span>}
            </label>
            <div className="mt-1">
              <Combobox
                value={user}
                onChange={setUser}
                items={users}
                getKey={(u) => u.user_id}
                getSearch={(u) => `${u.email || ""} ${u.name || ""} ${u.user_id}`}
                renderItem={(u, opts) => (
                  opts?.compact
                    ? (u.email || u.user_id)
                    : <span><span className="font-medium">{u.email || "(no email)"}</span>{u.name && <span className="text-neutral-500"> · {u.name}</span>}</span>
                )}
                placeholder="Search by email, name, or id…"
                emptyText={usersLoading ? "Loading users…" : "No matches"}
                testId="arm-trace-user"
              />
            </div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">
              Program {programsLoading && <span className="opacity-60 normal-case">loading…</span>}
            </label>
            <div className="mt-1">
              <Combobox
                value={program}
                onChange={setProgram}
                items={programs}
                disabled={!user}
                getKey={(p) => p.id}
                getSearch={(p) => `${p.code || ""} ${p.name || ""} ${p.provider || ""}`}
                renderItem={(p, opts) => (
                  opts?.compact
                    ? (p.code || p.name)
                    : <span><span className="font-mono">{p.code}</span>{p.name && <span className="text-neutral-500"> · {p.name}</span>}</span>
                )}
                placeholder={user ? "Search by code or name…" : "Pick a user first"}
                emptyText={programsLoading ? "Loading…" : "This user has no active programs"}
                testId="arm-trace-program"
              />
            </div>
          </div>
          {err && <div className="text-[12.5px] text-red-600">{err}</div>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost text-[12.5px]" disabled={busy}>Cancel</button>
            <button
              type="submit"
              className="btn btn-primary text-[12.5px]"
              disabled={busy || !user || !program}
              data-testid="arm-trace-submit"
            >
              {busy ? <span className="spinner" /> : "Arm trace"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CallRow({ ev }) {
  const [open, setOpen] = useState(false);
  const status = ev.status;
  const statusCls =
    status >= 200 && status < 300 ? "text-emerald-700"
    : status >= 300 && status < 400 ? "text-blue-700"
    : status >= 400 ? "text-red-700"
    : "text-neutral-500";
  return (
    <Fragment>
      <tr className="border-t border-black/5 hover:bg-neutral-50/60 cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <td className="py-1.5 px-3 text-neutral-400 w-6">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
        <td className="py-1.5 px-3 text-[11px] text-neutral-500 whitespace-nowrap">{fmtDate(ev.ts)}</td>
        <td className="py-1.5 px-3 text-[11px] font-mono uppercase">{ev.method || "—"}</td>
        <td className={`py-1.5 px-3 text-[11px] tabular ${statusCls}`}>{status ?? "—"}</td>
        <td className="py-1.5 px-3 text-[11.5px] truncate max-w-[460px] font-mono" title={ev.url}>{ev.url}</td>
        <td className="py-1.5 px-3 text-[11px] text-neutral-500">{ev.content_type || "—"}</td>
        <td className="py-1.5 px-3 text-[11px] tabular text-right">{fmtNum(ev.size_bytes)}</td>
        <td className="py-1.5 px-3 text-[11px] tabular text-right">{ev.duration_ms ? `${ev.duration_ms}ms` : "—"}</td>
      </tr>
      {open && (
        <tr className="border-t border-black/5 bg-neutral-50/40">
          <td colSpan={8} className="p-3 space-y-3 text-[11.5px]">
            {ev.request_headers && Object.keys(ev.request_headers).length > 0 && (
              <CallSection label="Request headers" data={ev.request_headers} />
            )}
            {ev.request_body && (
              <CallBlock label="Request body" text={String(ev.request_body)} />
            )}
            {ev.response_headers && Object.keys(ev.response_headers).length > 0 && (
              <CallSection label="Response headers" data={ev.response_headers} />
            )}
            {ev.body_skipped ? (
              <div className="text-neutral-500 italic">Binary body skipped ({ev.content_type}, {fmtNum(ev.size_bytes)} bytes).</div>
            ) : (
              ev.response_body != null && <CallBlock label="Response body" text={String(ev.response_body)} />
            )}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function CallSection({ label, data }) {
  return (
    <div>
      <div className="font-semibold text-neutral-600 mb-1">{label}</div>
      <div className="bg-white border border-black/5 rounded p-2 font-mono text-[11px] max-h-48 overflow-auto">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="truncate"><span className="text-neutral-500">{k}:</span> {String(v)}</div>
        ))}
      </div>
    </div>
  );
}

function CallBlock({ label, text }) {
  return (
    <div>
      <div className="font-semibold text-neutral-600 mb-1">{label} <span className="font-normal text-neutral-400">({text.length.toLocaleString()} chars)</span></div>
      <pre className="bg-white border border-black/5 rounded p-2 font-mono text-[11px] max-h-72 overflow-auto whitespace-pre-wrap break-all">{text}</pre>
    </div>
  );
}

function TraceDetail({ trace, onChanged, onClose }) {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0, page: 1 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [kind, setKind] = useState("");
  const [urlSubstr, setUrlSubstr] = useState("");
  const [syncId, setSyncId] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(trace);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/net-traces/${trace.id}`);
      setMeta(data);
    } catch { /* ignore */ }
  }, [trace.id]);

  const loadCalls = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = { page, page_size: 100 };
      if (kind) params.kind = kind;
      if (syncId) params.sync_id = syncId;
      if (urlSubstr) params.url_substr = urlSubstr;
      const { data } = await api.get(`/admin/net-traces/${trace.id}/calls`, { params });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally {
      setLoading(false);
    }
  }, [trace.id, page, kind, syncId, urlSubstr]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadCalls(); }, [loadCalls]);

  async function disarm() {
    try {
      await api.post(`/admin/net-traces/${trace.id}/disarm`);
      onChanged?.();
      loadMeta();
    } catch (e) { setErr(e?.response?.data?.detail || "disarm_failed"); }
  }

  async function deleteTrace() {
    if (!window.confirm("Delete this trace and all its captured calls? Permanent.")) return;
    try {
      await api.delete(`/admin/net-traces/${trace.id}`);
      onChanged?.();
      onClose?.();
    } catch (e) { setErr(e?.response?.data?.detail || "delete_failed"); }
  }

  const calls = (data.items || []).filter((e) => e.kind === "fetch" || e.kind === "xhr");
  const snapshots = (data.items || []).filter((e) => e.kind === "html_snapshot");
  const steps = (data.items || []).filter((e) => e.kind === "step_event");

  return (
    <div className="card overflow-hidden mt-4 bg-neutral-50/40" data-testid={`trace-detail-${trace.id}`}>
      <div className="px-5 py-3 border-b border-black/5 bg-white flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold tracking-tight truncate">
            {meta?.user_email || meta?.user_id} · {meta?.program_name || meta?.program_code}
          </div>
          <div className="text-[11.5px] text-neutral-500 truncate">
            {trace.id} · armed {fmtDate(meta?.armed_at)} {meta?.disarmed_at && `· disarmed ${fmtDate(meta.disarmed_at)}`}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusPill status={meta?.status || trace.status} />
          {meta?.status === "armed" && (
            <button onClick={disarm} className="btn btn-ghost text-[11.5px] py-1 px-2">Disarm</button>
          )}
          <button onClick={loadCalls} className="btn btn-ghost text-[11.5px] py-1 px-2">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Reload
          </button>
          <button onClick={deleteTrace} className="btn btn-ghost text-[11.5px] py-1 px-2 text-red-700 hover:text-red-900">
            <Trash2 size={12} /> Delete
          </button>
          <button onClick={onClose} className="text-neutral-400 hover:text-black p-1"><X size={14} /></button>
        </div>
      </div>

      <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
        <div><div className="text-[10.5px] uppercase tracking-[0.12em] opacity-60">Calls</div><div className="tabular font-bold mt-0.5">{fmtNum(meta?.call_count || 0)}</div></div>
        <div><div className="text-[10.5px] uppercase tracking-[0.12em] opacity-60">Last activity</div><div className="text-neutral-600 mt-0.5">{fmtDate(meta?.last_call_at)}</div></div>
        <div><div className="text-[10.5px] uppercase tracking-[0.12em] opacity-60">Sync sessions</div><div className="tabular font-bold mt-0.5">{(meta?.syncs || []).length}</div></div>
        <div><div className="text-[10.5px] uppercase tracking-[0.12em] opacity-60">Armed by</div><div className="text-neutral-600 mt-0.5 truncate">{meta?.armed_by_admin_email || "—"}</div></div>
      </div>

      {(meta?.syncs || []).length > 0 && (
        <div className="px-5 pb-3 flex flex-wrap items-center gap-2 text-[11.5px]">
          <span className="text-neutral-500">Sync sessions:</span>
          <button onClick={() => { setSyncId(""); setPage(1); }} className={`px-2 py-0.5 rounded border ${syncId === "" ? "bg-black text-white border-black" : "border-black/10 hover:bg-neutral-100"}`}>All</button>
          {meta.syncs.map((s) => (
            <button key={s.sync_id} onClick={() => { setSyncId(s.sync_id); setPage(1); }}
                    className={`px-2 py-0.5 rounded border font-mono text-[10.5px] ${syncId === s.sync_id ? "bg-black text-white border-black" : "border-black/10 hover:bg-neutral-100"}`}>
              {s.sync_id.slice(0, 20)} · {s.calls}
            </button>
          ))}
        </div>
      )}

      <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-t border-black/5 bg-white">
        <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} className="text-[11.5px] border border-neutral-300 rounded px-1.5 py-0.5 bg-white">
          <option value="">All event kinds</option>
          <option value="fetch">fetch</option>
          <option value="xhr">xhr</option>
          <option value="html_snapshot">HTML snapshot</option>
          <option value="step_event">step event</option>
        </select>
        <input
          type="text"
          placeholder="Filter URL substring…"
          value={urlSubstr}
          onChange={(e) => { setUrlSubstr(e.target.value); setPage(1); }}
          className="input text-[12px] py-1 max-w-[300px]"
        />
        <div className="ml-auto text-[11.5px] text-neutral-500">
          {fmtNum(data.total)} event{data.total === 1 ? "" : "s"}
          {data.total_pages > 1 && (
            <span className="ml-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">‹</button>
              <span className="px-2 tabular">{page} / {data.total_pages}</span>
              <button onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={page >= data.total_pages} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">›</button>
            </span>
          )}
        </div>
      </div>

      {err && <div className="px-5 py-2 text-[12.5px] text-red-600">{err}</div>}

      {(!kind || kind === "fetch" || kind === "xhr") && calls.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-left text-neutral-500 bg-neutral-50/60">
              <tr>
                <th className="py-1.5 px-3 w-6"></th>
                <th className="py-1.5 px-3 font-medium">Time</th>
                <th className="py-1.5 px-3 font-medium">Method</th>
                <th className="py-1.5 px-3 font-medium">Status</th>
                <th className="py-1.5 px-3 font-medium">URL</th>
                <th className="py-1.5 px-3 font-medium">Content-type</th>
                <th className="py-1.5 px-3 font-medium text-right">Size</th>
                <th className="py-1.5 px-3 font-medium text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((ev) => <CallRow key={ev.id} ev={ev} />)}
            </tbody>
          </table>
        </div>
      )}

      {(!kind || kind === "html_snapshot") && snapshots.length > 0 && (
        <div className="border-t border-black/5">
          <div className="px-5 py-2 text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold bg-neutral-50/60">HTML snapshots</div>
          {snapshots.map((s) => (
            <details key={s.id} className="px-5 py-2 border-t border-black/5 text-[11.5px]">
              <summary className="cursor-pointer text-neutral-700">{fmtDate(s.ts)} · <span className="font-mono">{s.url}</span> · {fmtNum(s.html ? s.html.length : 0)} chars</summary>
              <pre className="mt-2 bg-white border border-black/5 rounded p-2 font-mono text-[11px] max-h-[480px] overflow-auto whitespace-pre-wrap break-all">{s.html || ""}</pre>
            </details>
          ))}
        </div>
      )}

      {(!kind || kind === "step_event") && steps.length > 0 && (
        <div className="border-t border-black/5">
          <div className="px-5 py-2 text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold bg-neutral-50/60">Step events</div>
          <table className="w-full text-[11.5px]">
            <thead className="text-left text-neutral-500 bg-neutral-50/40">
              <tr>
                <th className="py-1.5 px-3 font-medium">Time</th>
                <th className="py-1.5 px-3 font-medium">Step</th>
                <th className="py-1.5 px-3 font-medium">Extractor</th>
                <th className="py-1.5 px-3 font-medium">Status</th>
                <th className="py-1.5 px-3 font-medium">Reason</th>
                <th className="py-1.5 px-3 font-medium">Fields</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((ev) => (
                <tr key={ev.id} className="border-t border-black/5">
                  <td className="py-1 px-3 text-neutral-500 whitespace-nowrap">{fmtDate(ev.ts)}</td>
                  <td className="py-1 px-3 font-mono">{ev.step_id || "—"}</td>
                  <td className="py-1 px-3 font-mono">{ev.extractor_id || "—"}</td>
                  <td className="py-1 px-3">{ev.step_status || "—"}</td>
                  <td className="py-1 px-3 text-neutral-500">{ev.reason || "—"}</td>
                  <td className="py-1 px-3 font-mono text-[11px] text-neutral-600 max-w-[420px] truncate" title={JSON.stringify(ev.fields || {})}>
                    {ev.fields ? JSON.stringify(ev.fields) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data.total === 0 && (
        <div className="px-5 py-8 text-[12.5px] text-neutral-500 text-center">
          No events yet. The user's extension will start appending once they open the program.
        </div>
      )}
    </div>
  );
}

export default function AdminNetTrace() {
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [armOpen, setArmOpen] = useState(false);
  const [expanded, setExpanded] = useState(null); // trace_id
  const [armedOnly, setArmedOnly] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = armedOnly ? { status: "armed" } : {};
      const { data } = await api.get("/admin/net-traces", { params });
      setTraces(data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally {
      setLoading(false);
    }
  }, [armedOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  const expandedTrace = useMemo(
    () => traces.find((t) => t.id === expanded) || null,
    [traces, expanded],
  );

  return (
    <AdminLayout
      activeTab="net-trace"
      title="Sync Trace"
      right={
        <div className="flex items-center gap-2">
          <label className="text-[12.5px] text-neutral-600 flex items-center gap-1.5">
            <input type="checkbox" checked={armedOnly} onChange={(e) => setArmedOnly(e.target.checked)} />
            Armed only
          </label>
          <button onClick={refresh} disabled={loading} className="btn btn-ghost text-[12.5px]">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => setArmOpen(true)} className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5">
            <Plus size={13} /> Arm trace
          </button>
        </div>
      }
    >
      <div data-testid="admin-net-trace-page">
        <div className="section-eyebrow">Admin</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Extension sync trace</h1>
        <p className="text-neutral-500 text-[13px] mt-1.5 max-w-2xl leading-relaxed">
          Arm a trace for a (user, program). On the user's next sync, the extension ships every
          captured network call, the rendered HTML snapshot, and step-machine events here so you
          can figure out what's actually happening.
        </p>

        {err && (
          <div className="mt-4 p-3 border rounded-[10px] text-[13px]" style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}>{err}</div>
        )}

        <div className="card overflow-hidden mt-6" data-testid="admin-net-trace-table">
          <table className="w-full text-[13px]">
            <thead className="text-left text-neutral-500 bg-neutral-50">
              <tr>
                <th className="py-2 px-3 font-medium w-6"></th>
                <th className="py-2 px-3 font-medium">User</th>
                <th className="py-2 px-3 font-medium">Program</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">Armed</th>
                <th className="py-2 px-3 font-medium">Last activity</th>
                <th className="py-2 px-3 font-medium text-right">Calls</th>
                <th className="py-2 px-3 font-medium">Armed by</th>
              </tr>
            </thead>
            <tbody>
              {traces.length === 0 && !loading && (
                <tr><td colSpan={8} className="py-8 text-center text-neutral-500 text-[13px]">No traces yet. Click "Arm trace" to start one.</td></tr>
              )}
              {traces.map((t) => {
                const isOpen = expanded === t.id;
                return (
                  <Fragment key={t.id}>
                    <tr className="border-t border-black/5 hover:bg-neutral-50/40 cursor-pointer" onClick={() => setExpanded(isOpen ? null : t.id)}>
                      <td className="py-2 px-3 text-neutral-400">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td className="py-2 px-3">
                        <div className="font-medium truncate">{t.user_email || t.user_id}</div>
                        <div className="text-[11px] text-neutral-500 font-mono truncate">{t.user_id}</div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="font-medium truncate">{t.program_name || t.program_code}</div>
                        <div className="text-[11px] text-neutral-500 font-mono truncate">{t.program_code}</div>
                      </td>
                      <td className="py-2 px-3"><StatusPill status={t.status} /></td>
                      <td className="py-2 px-3 text-[12px] text-neutral-600 whitespace-nowrap">{fmtDate(t.armed_at)}</td>
                      <td className="py-2 px-3 text-[12px] text-neutral-600 whitespace-nowrap">{fmtDate(t.last_call_at)}</td>
                      <td className="py-2 px-3 tabular text-right">{fmtNum(t.call_count || 0)}</td>
                      <td className="py-2 px-3 text-[11.5px] text-neutral-600 truncate" title={t.armed_by_admin_email || ""}>
                        {t.armed_by_admin_email || "—"}
                      </td>
                    </tr>
                    {isOpen && expandedTrace && (
                      <tr className="border-t border-black/5">
                        <td colSpan={8} className="p-0">
                          <TraceDetail trace={expandedTrace} onChanged={refresh} onClose={() => setExpanded(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ArmModal open={armOpen} onClose={() => setArmOpen(false)} onArmed={() => refresh()} />
    </AdminLayout>
  );
}
